import * as React from "react";
import { PreviewCard as PreviewCardPrimitive } from "@base-ui/react/preview-card";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-hover-card` to Base UI, whose equivalent
 * component is named **`preview-card`**.
 *
 * 25 files consume this and none of them changes. Unlike the components
 * migrated before it, almost nothing here is styling — Base UI moves four
 * separate pieces of the API, and each is absorbed below.
 *
 *  1. **The delays change owner.** Radix takes `openDelay` / `closeDelay` on
 *     the root; Base UI takes `delay` / `closeDelay` on the *trigger*. 24 of
 *     the 25 consumers pass both to the root, so `HoverCard` keeps accepting
 *     them there and hands them down through `HoverCardDelayContext`. Renaming
 *     `openDelay` at 24 call sites would be the alternative, for no gain.
 *  2. **`Content` is two elements now.** Base UI splits Radix's single content
 *     into `Positioner`, which owns placement, and `Popup`, which owns the box.
 *     `side` / `align` / `sideOffset` therefore stop being siblings of
 *     `className`, and `HoverCardContent` routes each to the right half.
 *  3. **The transform-origin variable is renamed.**
 *     `--radix-hover-card-content-transform-origin` becomes
 *     `--transform-origin`, and Base UI sets it on the positioner rather than
 *     the popup — custom properties inherit, so the popup can still read it.
 *     This is the difference with no symptom: an unresolvable
 *     `transform-origin` is not an error, it silently falls back to `50% 50%`,
 *     and the card animates from its own middle instead of from the trigger.
 *  4. **The open/close state attributes.** `data-[state=open]` becomes
 *     `data-[open]`, and the closing half becomes `data-[ending-style]` —
 *     Base UI has no `data-state="closed"` to key the exit animation on.
 *
 * The local `z-[1500]` override survives, but moves. It carried the comment
 * "Higher than dialog z-50 and MUI components to ensure proper stacking", and
 * z-index only does anything on a positioned element — under Radix that was the
 * content itself, under Base UI it is the positioner.
 */

/**
 * Carries the root's delays to the trigger, which is where Base UI wants them.
 * `undefined` means "no opinion", so Base UI applies its own defaults for the
 * one consumer that passes neither.
 */
/**
 * Radix defaults `collisionPadding` to `0`; Base UI defaults it to `5`. Left
 * alone, every card that reaches a viewport edge shifts inward by five pixels
 * — which is most of the inline-preview cards, since those hang off entity
 * links in wide tables and routinely clamp against the window.
 *
 * Pinned to Radix's value so the 25 consumers keep their placement. If the gap
 * is wanted it is a one-line change here, not 25.
 */
const RADIX_COLLISION_PADDING = 0;

const HoverCardDelayContext = React.createContext<{
  delay?: number;
  closeDelay?: number;
}>({});

type HoverCardProps = Omit<PreviewCardPrimitive.Root.Props, "children"> & {
  /**
   * Narrowed from Base UI's, which also accepts a render function receiving the
   * active trigger's `payload`. That form belongs to Base UI's `handle` API,
   * which has no Radix equivalent and therefore no caller here — and it cannot
   * be wrapped in the delay provider below, since a function is not a node.
   */
  children?: React.ReactNode;
  /** Radix's spelling; forwarded to the trigger as Base UI's `delay`. */
  openDelay?: number;
  /** Radix's spelling, and Base UI's — but on the trigger, not here. */
  closeDelay?: number;
};

function HoverCard({
  openDelay,
  closeDelay,
  children,
  ...props
}: HoverCardProps) {
  const delays = React.useMemo(
    () => ({ delay: openDelay, closeDelay }),
    [openDelay, closeDelay],
  );

  return (
    <PreviewCardPrimitive.Root {...props}>
      <HoverCardDelayContext.Provider value={delays}>
        {children}
      </HoverCardDelayContext.Provider>
    </PreviewCardPrimitive.Root>
  );
}

type HoverCardTriggerProps = Omit<
  PreviewCardPrimitive.Trigger.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

function HoverCardTrigger({
  asChild,
  render,
  children,
  ...props
}: HoverCardTriggerProps) {
  const { delay, closeDelay } = React.useContext(HoverCardDelayContext);
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <PreviewCardPrimitive.Trigger
      data-slot="hover-card-trigger"
      delay={delay}
      closeDelay={closeDelay}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </PreviewCardPrimitive.Trigger>
  );
}

/**
 * Radix's placement props live on the same element as the styling; Base UI's
 * live one level up. This keeps the Radix signature and splits it.
 */
type HoverCardContentProps = Omit<
  PreviewCardPrimitive.Popup.Props,
  | "className"
  | "side"
  | "align"
  | "sideOffset"
  | "alignOffset"
  | "collisionBoundary"
  | "collisionPadding"
> &
  Pick<
    PreviewCardPrimitive.Positioner.Props,
    | "side"
    | "align"
    | "sideOffset"
    | "alignOffset"
    | "collisionBoundary"
    | "collisionPadding"
  > & { className?: string };

function HoverCardContent({
  className,
  align = "center",
  side,
  sideOffset = 4,
  alignOffset,
  collisionBoundary,
  collisionPadding = RADIX_COLLISION_PADDING,
  ...props
}: HoverCardContentProps) {
  return (
    <PreviewCardPrimitive.Portal data-slot="hover-card-portal">
      <PreviewCardPrimitive.Positioner
        data-slot="hover-card-positioner"
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        // The local stacking override. It has to sit here rather than on the
        // popup: z-index only applies to a positioned element, and this is the
        // one Base UI positions.
        className="z-[1500]"
      >
        <PreviewCardPrimitive.Popup
          data-slot="hover-card-content"
          className={cn(
            "bg-popover text-popover-foreground data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 data-[ending-style]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-64 origin-(--transform-origin) rounded-md border p-4 shadow-md outline-hidden",
            className,
          )}
          {...props}
        />
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardContent };
